import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Drawer, Empty, Segmented, Space, Spin, Tag, Timeline, Typography, theme } from 'antd';
import { DesktopOutlined, MobileOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { usoSistemaService } from '../../services/usoSistemaService';
import { LinhaTempoUso, UsoPessoa, formatarMinutos, rotuloAcao, rotuloRota } from '../../types/usoSistema';

const { Text } = Typography;

/** Intervalo sem atividade que separa dois blocos de uso */
const PAUSA_MINUTOS = 5;

interface Bloco {
  tipo: 'bloco';
  inicio: dayjs.Dayjs;
  fim: dayjs.Dayjs;
  minutos: number;
  telas: { rota: string; minutos: number }[];
  celular: boolean;
}

interface Acao {
  tipo: 'acao';
  quando: dayjs.Dayjs;
  rotulo: string;
}

function montarLinhaTempo(dados: LinhaTempoUso): (Bloco | Acao)[] {
  // contas diferentes da mesma pessoa no mesmo minuto contam 1x
  const porMinuto = new Map<string, LinhaTempoUso['minutos'][number]>();
  dados.minutos.forEach(m => porMinuto.set(m.minuto, m));
  const minutos = [...porMinuto.values()].sort((a, b) => a.minuto.localeCompare(b.minuto));

  const blocos: Bloco[] = [];
  let telas = new Map<string, number>();
  let atual: Bloco | null = null;

  const fecharBloco = () => {
    if (!atual) return;
    atual.telas = [...telas.entries()]
      .map(([rota, n]) => ({ rota, minutos: n }))
      .sort((a, b) => b.minutos - a.minutos);
    blocos.push(atual);
  };

  minutos.forEach(m => {
    const instante = dayjs(m.minuto);
    if (!atual || instante.diff(atual.fim, 'minute') > PAUSA_MINUTOS) {
      fecharBloco();
      atual = { tipo: 'bloco', inicio: instante, fim: instante, minutos: 0, telas: [], celular: false };
      telas = new Map();
    }
    atual.fim = instante;
    atual.minutos += 1;
    atual.celular ||= m.dispositivo === 'celular';
    const rota = m.rota ?? '(desconhecida)';
    telas.set(rota, (telas.get(rota) ?? 0) + 1);
  });
  fecharBloco();

  const acoes: Acao[] = dados.acoes.map(a => ({
    tipo: 'acao',
    quando: dayjs(a.created_at),
    rotulo: rotuloAcao(a.entidade, a.operacao),
  }));

  return [...blocos, ...acoes].sort((a, b) => {
    const ta = a.tipo === 'bloco' ? a.inicio : a.quando;
    const tb = b.tipo === 'bloco' ? b.inicio : b.quando;
    return ta.valueOf() - tb.valueOf();
  });
}

interface LinhaTempoDrawerProps {
  pessoa: UsoPessoa | null;
  dias: string[];
  onClose: () => void;
}

export function LinhaTempoDrawer({ pessoa, dias, onClose }: LinhaTempoDrawerProps) {
  const { token } = theme.useToken();
  const [dia, setDia] = useState<string>('');

  useEffect(() => {
    if (!pessoa) return;
    const diasComUso = dias.filter(d => pessoa.por_dia[d]);
    setDia(diasComUso[diasComUso.length - 1] ?? dias[dias.length - 1]);
  }, [pessoa, dias]);

  const profileIds = pessoa?.contas.map(c => c.profile_id) ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ['linha-tempo-uso', profileIds, dia],
    queryFn: () => usoSistemaService.buscarLinhaTempo(profileIds, dia),
    enabled: !!pessoa && !!dia,
  });

  const itens = useMemo(() => (data ? montarLinhaTempo(data) : []), [data]);

  return (
    <Drawer
      title={pessoa?.nome}
      open={!!pessoa}
      onClose={onClose}
      width={Math.min(520, window.innerWidth)}
      destroyOnHidden
    >
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ overflowX: 'auto' }}>
          <Segmented
            value={dia}
            onChange={valor => setDia(String(valor))}
            options={dias.map(d => {
              const uso = pessoa?.por_dia[d];
              return {
                value: d,
                label: (
                  <div style={{ padding: '2px 0', lineHeight: 1.3 }}>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>{dayjs(d).format('ddd')}</div>
                    <div>{dayjs(d).format('DD/MM')}</div>
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 3,
                        margin: '2px auto 0',
                        background: uso ? token.colorSuccess : 'transparent',
                      }}
                    />
                  </div>
                ),
              };
            })}
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><Spin /></div>
        ) : itens.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sem atividade neste dia" />
        ) : (
          <Timeline
            items={itens.map(item =>
              item.tipo === 'bloco'
                ? {
                    color: token.colorInfo,
                    icon: item.celular ? <MobileOutlined /> : <DesktopOutlined />,
                    content: (
                      <Space orientation="vertical" size={4}>
                        <Text strong>
                          {item.inicio.format('HH:mm')}–{item.fim.add(1, 'minute').format('HH:mm')}
                          <Text type="secondary"> · {formatarMinutos(item.minutos)} ativo</Text>
                        </Text>
                        <Space size={4} wrap>
                          {item.telas.map(t => (
                            <Tag key={t.rota} bordered={false}>
                              {rotuloRota(t.rota)} · {formatarMinutos(t.minutos)}
                            </Tag>
                          ))}
                        </Space>
                      </Space>
                    ),
                  }
                : {
                    color: token.colorSuccess,
                    icon: <ThunderboltOutlined />,
                    content: (
                      <Text>
                        <Text type="secondary">{item.quando.format('HH:mm')}</Text> {item.rotulo}
                      </Text>
                    ),
                  }
            )}
          />
        )}
      </Space>
    </Drawer>
  );
}
